import { isSameDay, subDays } from 'date-fns'

/**
 * Discriminated representation of a relative timestamp, decoupled from i18n so it can be unit-tested
 * without a translation runtime. The rendering component maps each `kind` to a translation key.
 */
export type RelativeTimePart =
  | { kind: 'just_now' }
  | { kind: 'minutes', value: number }
  | { kind: 'hours', value: number }
  | { kind: 'yesterday' }
  | { kind: 'date', date: Date }

const MS_PER_MINUTE = 60_000

/**
 * Computes a human-friendly relative time bucket for [date] relative to [now]:
 * `< 1 min` → just now, `< 1 h` → minutes, same day → hours, previous day → yesterday, otherwise a date.
 */
export const getRelativeTimePart = (date: Date, now: Date = new Date()): RelativeTimePart => {
  const diffMinutes = Math.floor((now.getTime() - date.getTime()) / MS_PER_MINUTE)

  if (diffMinutes < 1) return { kind: 'just_now' }
  if (diffMinutes < 60) return { kind: 'minutes', value: diffMinutes }
  if (isSameDay(date, now)) return { kind: 'hours', value: Math.floor(diffMinutes / 60) }
  if (isSameDay(date, subDays(now, 1))) return { kind: 'yesterday' }

  return { kind: 'date', date }
}
